import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import type { Order } from '../types/orders';

interface SoldProduct {
  id: number;
  name: string;
  platform?: string;
  quantity_sold: number;
  total_value: number;
  price: number;
}

interface DailySales {
  date: string;
  total: number;
}

/**
 * Generates a PDF for the End of Day (EOD) report
 * @param reportDate - The date of the report
 * @param orders - All orders from the day
 * @param soldProducts - Products sold during the day
 * @param totalSales - Total sales amount for the day
 * @param dailySales - Daily sales data for trend analysis
 * @returns PDF document as a blob URL for download
 */
export const generateEODReportPDF = (
  reportDate: Date,
  orders: Order[],
  soldProducts: SoldProduct[],
  totalSales: number,
  dailySales: DailySales[]
): string => {
  // Create new PDF document
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  
  // Reusable function to check if we need a new page
  const ensureSpace = (neededSpace: number): void => {
    // @ts-ignore - jsPDF types are incomplete
    const y = doc.lastAutoTable?.finalY || doc.internal.getVerticalCoordinateString();
    if (y + neededSpace > pageHeight - margin) {
      doc.addPage();
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `${format(reportDate, 'MMMM d, yyyy')} - EOD Report - Page ${doc.getNumberOfPages()}`,
        pageWidth / 2,
        10,
        { align: 'center' }
      );
    }
  };
  
  // Cover Page
  // =========================================================
  // Header with date and title
  doc.setFontSize(24);
  doc.setTextColor(88, 28, 135); // Purple color for brand
  doc.text('Big Vision Games', pageWidth / 2, 40, { align: 'center' });
  
  doc.setFontSize(18);
  doc.setTextColor(60, 60, 60);
  doc.text('End of Day Sales Report', pageWidth / 2, 55, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text(format(reportDate, 'MMMM d, yyyy'), pageWidth / 2, 65, { align: 'center' });
  
  // Summary box
  doc.setDrawColor(88, 28, 135);
  doc.setFillColor(247, 243, 254);
  doc.roundedRect(margin, 80, pageWidth - (margin * 2), 50, 5, 5, 'FD');
  
  doc.setFontSize(14);
  doc.setTextColor(60, 60, 60);
  doc.text('Daily Summary', margin + 10, 95);
  
  doc.setFontSize(12);
  doc.text(`Total Sales: £${totalSales.toFixed(2)}`, margin + 20, 110);
  doc.text(`Orders: ${orders.length}`, margin + 20, 120);
  
  const totalItemsSold = soldProducts.reduce((sum, p) => sum + p.quantity_sold, 0);
  doc.text(`Items Sold: ${totalItemsSold}`, pageWidth - margin - 70, 110);
  
  const avgOrderValue = orders.length > 0 ? totalSales / orders.length : 0;
  doc.text(`Average Order Value: £${avgOrderValue.toFixed(2)}`, pageWidth - margin - 70, 120);
  
  // Add timestamp
  doc.setFontSize(10);
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy h:mm a')}`, pageWidth - margin, pageHeight - 20, { align: 'right' });
  
  // Page number
  doc.text('Page 1', pageWidth / 2, pageHeight - 20, { align: 'center' });
  
  // Top Products Section
  // =========================================================
  doc.addPage();
  let currentY = margin;
  
  // Header for each page
  doc.setFontSize(10);
  doc.setTextColor(128, 128, 128);
  doc.text(
    `${format(reportDate, 'MMMM d, yyyy')} - EOD Report - Page ${doc.getNumberOfPages()}`,
    pageWidth / 2,
    10,
    { align: 'center' }
  );
  
  // Top Products heading
  doc.setFontSize(16);
  doc.setTextColor(88, 28, 135);
  doc.text('Top Selling Products', margin, currentY);
  currentY += 10;
  
  // Sort products by total value
  const topProducts = [...soldProducts].sort((a, b) => b.total_value - a.total_value);
  
  // Add product table
  autoTable(doc, {
    startY: currentY,
    head: [['Rank', 'Product', 'Platform', 'Units Sold', 'Unit Price', 'Total', '% of Sales']],
    body: topProducts.map((product, index) => [
      index + 1,
      product.name,
      product.platform || '-',
      product.quantity_sold,
      `£${product.price.toFixed(2)}`,
      `£${product.total_value.toFixed(2)}`,
      `${((product.total_value / totalSales) * 100).toFixed(1)}%`
    ]),
    headStyles: {
      fillColor: [88, 28, 135],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { cellWidth: 15, halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' }
    },
    margin: { top: 30, bottom: 30 }
  });
  
  // Platform Distribution Section
  // =========================================================
  ensureSpace(150);
  
  // @ts-ignore - jsPDF types are incomplete
  currentY = doc.lastAutoTable?.finalY || currentY;
  currentY += 15;
  
  doc.setFontSize(16);
  doc.setTextColor(88, 28, 135);
  doc.text('Platform Distribution', margin, currentY);
  currentY += 10;
  
  // Calculate platform distribution
  const platforms = ['PlayStation 5', 'PlayStation 4', 'Xbox Series X/S', 'Xbox One', 'Nintendo Switch', 'PC'];
  const platformData = platforms.map(platform => {
    const count = soldProducts
      .filter(p => p.platform === platform)
      .reduce((sum, p) => sum + p.quantity_sold, 0);
    const total = soldProducts.reduce((sum, p) => sum + p.quantity_sold, 0);
    const percentage = total > 0 ? (count / total) * 100 : 0;
    const value = soldProducts
      .filter(p => p.platform === platform)
      .reduce((sum, p) => sum + p.total_value, 0);
    
    return [platform, count, `${percentage.toFixed(1)}%`, `£${value.toFixed(2)}`];
  });
  
  autoTable(doc, {
    startY: currentY,
    head: [['Platform', 'Units Sold', '% of Units', 'Total Value']],
    body: platformData,
    headStyles: {
      fillColor: [88, 28, 135],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    columnStyles: {
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'right' }
    }
  });
  
  // Loyalty Points Section
  // =========================================================
  ensureSpace(120);
  
  // @ts-ignore - jsPDF types are incomplete
  currentY = doc.lastAutoTable?.finalY || currentY;
  currentY += 15;
  
  doc.setFontSize(16);
  doc.setTextColor(88, 28, 135);
  doc.text('Loyalty Program', margin, currentY);
  currentY += 10;
  
  const totalPointsUsed = orders.reduce((sum, order) => sum + (order.points_used || 0), 0);
  const totalPointsEarned = orders.reduce((sum, order) => sum + (order.points_earned || 0), 0);
  const pointsValue = totalPointsUsed / 100; // 100 points = £1
  
  autoTable(doc, {
    startY: currentY,
    head: [['Points Used', 'Points Earned', 'Net Points', 'Points Value']],
    body: [[
      totalPointsUsed,
      totalPointsEarned,
      totalPointsEarned - totalPointsUsed,
      `£${pointsValue.toFixed(2)}`
    ]],
    headStyles: {
      fillColor: [88, 28, 135],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { halign: 'center' },
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'center' }
    }
  });
  
  // Orders List Section
  // =========================================================
  doc.addPage();
  
  // Header for new page
  doc.setFontSize(10);
  doc.setTextColor(128, 128, 128);
  doc.text(
    `${format(reportDate, 'MMMM d, yyyy')} - EOD Report - Page ${doc.getNumberOfPages()}`,
    pageWidth / 2,
    10,
    { align: 'center' }
  );
  
  doc.setFontSize(16);
  doc.setTextColor(88, 28, 135);
  doc.text('Orders List', margin, margin);
  
  // Create flat list of order data for table
  const orderTableData = orders.map(order => {
    const time = format(new Date(order.created_at), 'h:mm a');
    const itemCount = order.order_items.length;
    const pointsUsed = order.points_used || 0;
    const pointsEarned = order.points_earned || 0;
    
    return [
      order.id,
      time,
      itemCount,
      `£${order.total_amount.toFixed(2)}`,
      pointsUsed > 0 ? pointsUsed : '-',
      pointsEarned
    ];
  });
  
  // Add orders table with pagination
  autoTable(doc, {
    startY: margin + 10,
    head: [['Order ID', 'Time', 'Items', 'Total', 'Points Used', 'Points Earned']],
    body: orderTableData,
    headStyles: {
      fillColor: [88, 28, 135],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 'auto', halign: 'center' },
      3: { cellWidth: 'auto', halign: 'right' },
      4: { cellWidth: 'auto', halign: 'center' },
      5: { cellWidth: 'auto', halign: 'center' }
    },
    didDrawPage: (data) => {
      // Add header to each new page
      if (data.pageNumber > 1) {
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `${format(reportDate, 'MMMM d, yyyy')} - EOD Report - Page ${doc.getNumberOfPages()}`,
          pageWidth / 2,
          10,
          { align: 'center' }
        );
      }
    }
  });
  
  // Return the PDF as a data URL for download
  return doc.output('datauristring');
}; 